import React, { FC, useState } from 'react';
import { BUTTON_TYPE, ButtonComponent } from '../ButtonComponent';
import { useTranslation } from 'react-i18next';
import { IModalWindow, MODAL_TYPE, ModalWindow } from '../modal/ModalWindow';
import { cancelApplication } from '../../api/new-application-api-service';
import { useNavigate, useParams } from 'react-router-dom';
import { useLocation } from 'react-router';
import { ReviewFooterActions } from './ReviewFooterActions';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { STATUSES } from '../../views/login/Login';
import { DropdownDisabledPlaceholder } from '../DropdownDisabledPlaceholder';
import { NOTIFICATION_TYPES, NotificationModal } from './NotificationModal';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export const ReviewFooter: FC = () => {
  const { t } = useTranslation();
  const { applicationId } = useParams<{ applicationId: string }>();
  const { state } = useLocation();
  const navigate = useNavigate();

  const { merchantState: { applicationData }} = useMerchants();

  const [modalState, setModalState] = useState<IModalWindow>({
    isOpen: false
  });

  const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

  const handleCancel = (): void => {
    const handleSuccess = (): void => navigate(`/accounts/applications?pageNumber=${state?.pageNumber}&pageSize=${state?.pageSize}`);

    setModalState({
      isOpen: true,
      modalType: MODAL_TYPE.DECLINE,
      modalTitle: t('NEW_APPLICATION.CANCEL_MODAL_TITLE'),
      modalText: t('NEW_APPLICATION.CANCEL_MODAL_TEXT'),
      confirmButtonText: 'Confirm',
      onConfirm: async (): Promise<void> => {
        await cancelApplication({ applicationId, handleSuccess, onError: closeModal });
        closeModal();
      },
      onCancel: closeModal,
    });
  };

  const successNotificationModal = (): void => {
    setModalState({
      isOpen: true,
      modalType: MODAL_TYPE.APPROVE,
      modalTitle: 'Notification',
      modalText: 'Notification has been sent successfully.',
      confirmButtonText: 'Ok',
      onConfirm: closeModal,
    });
  };

  const errorNotificationModal = (): void => {
    setModalState({
      isOpen: true,
      modalType: MODAL_TYPE.DECLINE,
      modalTitle: 'Notification',
      modalText: 'Failed to send notification.',
      confirmButtonText: 'Ok',
      onConfirm: closeModal,
    });
  };

  const openNotificationModal = (name: NOTIFICATION_TYPES): void => {
    setModalState({
      isOpen: true,
      modalTitle: 'Notification',
      backdrop: 'static',
      contentClassName: 'd-block',
      minHeight: 100,
      content: <NotificationModal name={name} onClose={closeModal} onSuccess={successNotificationModal} onError={errorNotificationModal} />
    });
  };

  return (
    <div
      className="d-flex flex-column-reverse flex-sm-row align-items-sm-center justify-content-sm-between pt-5 mb-20 pb-10">
      <ButtonComponent
        buttonType={BUTTON_TYPE.TRANSPARENT}
        buttonName={t('APPLICATION_DETAILS.LABEL.CANCEL_APPLICATION')}
        additionalClass="mt-10 mt-sm-0"
        onButtonClick={handleCancel}
      />

      <div className="d-flex flex-column flex-sm-row">
        <ButtonComponent
          additionalClass="btn-sm mb-5 mb-sm-0 me-sm-5"
          onButtonClick={(): void => openNotificationModal(NOTIFICATION_TYPES.ACCOUNT_MANAGER)}
          isDisabled={!applicationData?.accountManagerId}
          isTooltip={!applicationData?.accountManagerId}
          tooltipText={t('APPLICATION_DETAILS.LABEL.NOTIFY_ACCOUNT_MANAGER_TOOLTIP')}
        >
          <>
            <FontAwesomeIcon icon={faEnvelope} width={12} className="me-2"/>
            <span>{t('APPLICATION_DETAILS.LABEL.NOTIFY_ACCOUNT_MANAGER')}</span>
          </>
        </ButtonComponent>
        <ButtonComponent
          additionalClass="btn-sm mb-5 mb-sm-0 me-sm-5"
          onButtonClick={(): void => openNotificationModal(NOTIFICATION_TYPES.APPLICANT)}
        >
          <>
            <FontAwesomeIcon icon={faEnvelope} width={12} className="me-2"/>
            <span>Notify applicant</span>
          </>
        </ButtonComponent>

        {
          applicationData?.statusName === STATUSES.SUBMITTED
            ? <ReviewFooterActions />
            : <DropdownDisabledPlaceholder name={t('APPLICATION_DETAILS.LABEL.APPLICATION_REVIEW')} />
        }
      </div>



      <ModalWindow
        {...modalState}
        onClose={closeModal}
      />
    </div>
  );
};